import format from 'date-fns/format'
import deepEqual from 'fast-deep-equal'
import { Dictionary } from 'lodash'
import { makeAutoObservable, reaction, toJS } from 'mobx'
import { ChangeEvent, useState } from 'react'
import { TableStore } from '../../../components/table2/table_store'
import { FilterI, FilterValue } from '../../../constants/filter'
import { removeIncompleteSelectComponentFilters } from '../../../util/transformSelectComponentFilters'

interface FilterHook {
    current: FilterValues
    active: ActiveFilters
    loadDefault: any
    loadDefaultMultiple: any
    loadCustom: any
    saveCustom: any
    editCustom: any
    deleteCustom: any
    openLoadModal: () => void
    openSaveModal: () => void
    openManageModal: () => void
    loadModalOpen: boolean
    saveModalOpen: boolean
    manageModalOpen: boolean
    setLoadModalOpen: (v: boolean) => any
    setSaveModalOpen: (v: boolean) => any
    setManageModalOpen: (v: boolean) => any
    apply: () => void
    clear: () => void
    isActive: (filter: FilterI) => boolean
    toggle: (filter: FilterI) => (e: ChangeEvent<HTMLInputElement>) => void
    set: (filter: FilterI, v: FilterValue) => void
    get: (filter: FilterI) => FilterValue | undefined
    remove: (filter: Pick<FilterI, 'key'>) => void
    dirty: DirtyFilterValues
    count: number
}

export interface FilterValues {
    [key: string]: FilterValue
}

interface DirtyFilterValues {
    [key: string]: FilterValue | undefined
}

interface ActiveFilters {
    [key: string]: boolean
}

export interface SavedFilter {
    id: number
    name: string
    table_name: string
    filters: Dictionary<FilterValue>
    // filters: FilterI[]
    profile_id: number
    created_at: string
    updated_at: string
}

// Cleans a set of dirty filter values by removing any undefined values.
// Dirty filter values need to keep undefined values so that we know
// which filters to remove from the current set.
const cleanValues = (dirtyValues: DirtyFilterValues): FilterValues => {
    return Object.keys(dirtyValues).reduce((result: FilterValues, key) => {
        const value = dirtyValues[key]
        if (value !== undefined) {
            result[key] = value
        }

        return result
    }, {})
}

export const useFilter = (
    onChange: (v: FilterValues) => any,
    save: (name: string, payload: object) => any,
    deleteFilter?: (v: number) => any,
    edit?: (id: number, payload: any) => any,
): FilterHook => {
    const [active, setActive] = useState<ActiveFilters>({})
    const [dirty, setDirty] = useState<DirtyFilterValues>({})
    const [current, setCurrent] = useState<FilterValues>({})
    const [loadModalOpen, setLoadModalOpen] = useState<boolean>(false)
    const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false)
    const [manageModalOpen, setManageModalOpen] = useState<boolean>(false)

    const openLoadModal = (): void => {
        setLoadModalOpen(true)
    }
    const openSaveModal = (): void => {
        setSaveModalOpen(true)
    }
    const openManageModal = (): void => {
        setManageModalOpen(true)
    }

    const apply = (): void => {
        emitPayload()
        const filters: DirtyFilterValues = {
            ...current,
            ...dirty,
        }

        setCurrent(cleanValues(filters))
        setDirty({})
    }

    const clear = (): void => {
        setActive({})
        setDirty({})
        setCurrent({})

        emitPayload({})
    }

    const emitPayload = (override?: FilterValues): FilterValues => {
        const payload: FilterValues = override ? override : Object.assign(current, dirty)

        // Strip out any undefined
        Object.keys(payload).filter((key) => {
            const tmp: FilterValue = payload[key]
            return typeof tmp !== undefined
        })

        if (onChange) {
            onChange(payload)
        } else {
            console.warn('No onChange function specified')
        }

        return payload
    }

    /**
     * Creates the initial value for the filter
     *
     * @param {FilterI} filter
     * @return {FilterValue}
     */
    const initial = (filter: FilterI): FilterValue => {
        switch (filter.type) {
            case 'select':
                return filter.options[0].value
            case 'date':
                return format(new Date(), 'yyyy-MM-dd').toString()
            case 'number':
            case 'nullable-number':
                if (filter.operators && filter.operators.length > 0) {
                    const op = filter.operators[0]
                    if (op !== '=') {
                        return { [op]: 0 }
                    }
                }
                return 0
            case 'select-component':
                console.error('do something for this')
                throw new Error('Not implemented')
            default:
                console.error('Cannot get initial state for filter type', filter)
                throw new Error('Cannot get initial state for filter type')
        }
    }

    const toggle = (filter: FilterI) => {
        return (e: ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked
            setActive({ ...active, [filter.key]: !!checked })
            setDirty({ ...dirty, [filter.key]: checked ? initial(filter) : undefined })
        }
    }

    // @todo use custom filter type
    const loadCustom = (customFilters: SavedFilter[], filterId: number) => {
        console.log('Custom filters', customFilters)
        if (customFilters.length == 0) {
            return
        }

        clear()

        if (!filterId) {
            filterId = 0
        }

        const _active: ActiveFilters = {}
        const _current: FilterValues = {}
        // Saved filters persisted before incomplete selectors were stripped can
        // still hold one. Requests are sanitized either way, but an unsanitized
        // entry here renders as a tag with no value.
        const savedFilters = removeIncompleteSelectComponentFilters(customFilters[filterId].filters)
        Object.keys(savedFilters ?? {}).forEach((key) => {
            _active[key] = true
            // @ts-ignore
            _current[key] = savedFilters[key]
        })

        setActive(_active)
        setCurrent(_current)

        emitPayload({ ..._current })
    }

    const loadDefault = (filter: FilterI, v: FilterValue) => {
        if (!filter) {
            return
        }

        const _active: ActiveFilters = {}
        const _current: FilterValues = {}
        _active[filter.key] = true
        _current[filter.key] = v

        setActive(_active)
        setCurrent(_current)

        emitPayload({ ..._current })
    }

    interface FilterItem {
        filter: FilterI
        value: FilterValue
    }

    const loadDefaultMultiple = (multipleFilters: FilterItem[]) => {
        if (multipleFilters.length == 0) {
            return
        }

        const _active: ActiveFilters = {}
        const _current: FilterValues = {}

        multipleFilters.forEach((filter) => {
            _active[filter.filter.key] = true
            _current[filter.filter.key] = filter.value
        })

        setActive(_active)
        setCurrent(_current)

        console.log('tries to load active, current', _active, _current)

        emitPayload({ ..._current })
    }

    const set = (filter: FilterI, v: FilterValue) => {
        setDirty({
            ...dirty,
            [filter.key]: v,
        })
    }

    const saveCustom = (name: string): FilterValues => {
        const res = {
            ...current,
            ...dirty,
        }
        save(name, res)
        apply()

        return current
    }

    const editCustom = (id?: number, payload?: any): FilterValues => {
        console.log('[useFilter]: editCustom', id)

        const result = {
            ...current,
            ...dirty,
        }

        edit && id ? edit(id, payload ?? result) : null
        apply()

        return current
    }

    const deleteCustom = (id: number): void => {
        deleteFilter ? deleteFilter(id) : null
        apply() // ?
    }

    const isActive = (filter: FilterI): boolean => !!active[filter.key]

    const get = (filter: FilterI): FilterValue | undefined => {
        if (dirty.hasOwnProperty(filter.key)) {
            return dirty[filter.key]
        } else {
            return current[filter.key]
        }
    }

    const remove = (filter: Pick<FilterI, 'key'>): void => {
        // Remove the passed filter key
        const { [filter.key]: removed, ...c } = current

        setCurrent(c)
        setActive({
            ...active,
            [filter.key]: false,
        })
        emitPayload(c)
    }

    return {
        current,
        active,
        loadDefault,
        loadDefaultMultiple,
        loadCustom,
        saveCustom,
        editCustom,
        deleteCustom,
        openLoadModal,
        openSaveModal,
        openManageModal,
        loadModalOpen,
        saveModalOpen,
        manageModalOpen,
        setLoadModalOpen,
        setSaveModalOpen,
        setManageModalOpen,
        apply,
        clear,
        isActive,
        toggle,
        set,
        get,
        remove,
        dirty,
        count: Object.keys(current).filter((k) => typeof current[k] !== 'undefined').length,
    }
}

export class FilterStore {
    open = false
    tableStore: TableStore<any>
    active: ActiveFilters = {}
    dirty: DirtyFilterValues = {}
    current: FilterValues = {}
    loadModalOpen = false
    saveModalOpen = false
    manageModalOpen = false
    allowedFilters: FilterI[]

    constructor(tableStore: TableStore<any>, allowedFilters: FilterI[]) {
        this.tableStore = tableStore
        this.allowedFilters = allowedFilters
        this.active = Object.keys(tableStore.params.filter ?? {}).reduce((acc, key) => {
            acc[key] = true
            return acc
        }, {})
        this.current = tableStore.params.filter ?? {}
        console.log(
            '[debug] init filters. Source',
            toJS(tableStore.params.filter),
            toJS(this.active),
            toJS(this.current),
        )

        this.updateFromTableStore()

        makeAutoObservable(this)

        // Set up a reaction to update when tableStore.params.filter changes
        reaction(
            () => this.tableStore.params.filter,
            (newFilter) => {
                this.updateFromTableStore()
            },
        )
    }

    private updateFromTableStore() {
        const newFilter = this.tableStore.params.filter ?? {}
        this.active = Object.keys(newFilter).reduce((acc, key) => {
            acc[key] = true
            return acc
        }, {})
        this.current = newFilter
    }

    setOpen(open: boolean | ((open: boolean) => boolean)) {
        const newOpen = typeof open == 'function' ? open(this.open) : open
        if (this.open != newOpen) {
            this.open = newOpen
        }
    }

    toggleOpen() {
        this.setOpen((p) => !p)
    }

    setActive(active: ActiveFilters | ((currentParams: ActiveFilters) => ActiveFilters)) {
        const newParams = typeof active === 'function' ? active(this.active) : active
        if (!deepEqual(this.active, newParams)) {
            this.active = newParams
        }
    }

    setDirty(dirty: DirtyFilterValues | ((current: DirtyFilterValues) => DirtyFilterValues)) {
        const newDirty = typeof dirty === 'function' ? dirty(this.dirty) : dirty
        if (!deepEqual(this.dirty, newDirty)) {
            this.dirty = newDirty
        }
    }

    setCurrent(current: FilterValues) {
        this.current = current
    }

    clear() {
        this.active = {}
        this.dirty = {}
        this.current = {}
    }

    get activeFilters() {
        return this.tableStore.params.filter ?? {}
    }

    get isActive() {
        return (filter: FilterI) => {
            console.log(
                'active',
                filter.key,
                !!this.active[filter.key],
                toJS(this.active),
                toJS(this.current),
                toJS(this.dirty),
            )
            return !!this.active[filter.key]
        }
    }

    get get() {
        return (filter: FilterI): FilterValue | undefined => {
            if (this.dirty.hasOwnProperty(filter.key)) {
                return this.dirty[filter.key]
            } else {
                return this.current[filter.key]
            }
        }
    }

    set(filter: FilterI, v: FilterValue) {
        this.dirty[filter.key] = v
    }

    toggle(filter: FilterI) {
        return (e: ChangeEvent<HTMLInputElement>) => {
            const checked = e.target.checked

            this.active[filter.key] = !!checked
            this.dirty[filter.key] = checked ? initial(filter) : undefined
        }
    }

    /** The filter state is saveable if it has one or more filters */
    get saveable(): boolean {
        return (
            Object.keys({
                ...this.current,
                ...this.dirty,
            }).length > 0
        )
    }

    get allowedFiltersByKey() {
        return this.allowedFilters.reduce((acc, filter) => {
            acc[filter.key] = filter
            return acc
        }, {})
    }
}

/**
 * Creates the initial value for the filter
 *
 * @param {FilterI} filter
 * @return {FilterValue}
 */
const initial = (filter: FilterI): FilterValue => {
    switch (filter.type) {
        case 'select':
            return filter.options[0].value
        case 'date':
            return format(new Date(), 'yyyy-MM-dd').toString()
        case 'number':
        case 'nullable-number':
            if (filter.operators && filter.operators.length > 0) {
                const op = filter.operators[0]
                if (op !== '=') {
                    return { [op]: 0 }
                }
            }
            return 0
        case 'select-component':
            console.error('do something for this')
            throw new Error('Not implemented')
        default:
            console.error('Cannot get initial state for filter type', filter)
            throw new Error('Cannot get initial state for filter type')
    }
}
